import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Yaslie — sélecteur d'offre à paliers.
 *
 * Le Liquid rend autant de blocs « exemplaire » que le plus grand palier en
 * réclame ; ce composant n'en affiche que les N premiers selon le palier
 * choisi. Changer de palier ne recharge donc rien et ne perd pas les
 * sélections déjà faites.
 *
 * Un palier à 1 exemplaire rebascule sur le parcours natif du thème
 * (variant-picker + boutons d'achat) : inutile de dupliquer ce qu'Horizon
 * fait déjà bien.
 *
 * L'ajout suit le protocole de `product-form.js` : requête sur
 * `cart_add_url`, puis `CartLinesUpdateEvent` avec une promesse résolue à
 * partir du panier rafraîchi — c'est ce que `cart-drawer` et `cart-icon`
 * écoutent pour se réafficher et s'ouvrir.
 */
class YaslieBundle extends Component {
  /** @type {{options: {name: string, values: string[]}[], variants: {id: number, available: boolean, options: string[]}[], perUnitTemplate: string, savingTemplate: string, unitTemplate: string, freeLabel: string}} */
  #data = { options: [], variants: [] };

  /** @type {{index: number, units: number, label: string, badge: string, total: number, totalMoney: string, saving: number, savingMoney: string, perUnitMoney: string}[]} */
  #tiers = [];

  #submitting = false;

  connectedCallback() {
    super.connectedCallback();

    if (!this.#readJson()) return;

    for (const input of this.querySelectorAll('[data-yaslie-offer-input]')) {
      input.addEventListener('change', this.#handleTierChange);
    }

    for (const select of this.querySelectorAll('[data-yaslie-select]')) {
      select.addEventListener('change', this.#handleSelectChange);
    }

    this.querySelector('[data-yaslie-bundle-submit]')?.addEventListener('click', this.#handleSubmit);

    // Chaque exemplaire démarre sur la première combinaison réellement
    // disponible, jamais sur une variante en rupture.
    for (const unit of this.#allUnits) this.#preselect(unit);

    this.#applyTier(this.#checkedTier);
  }

  #readJson() {
    const dataEl = this.querySelector('[data-yaslie-bundle-data]');
    const tiersEl = this.querySelector('[data-yaslie-tiers]');
    if (!dataEl?.textContent || !tiersEl?.textContent) return false;

    try {
      this.#data = JSON.parse(dataEl.textContent);
      // Le Liquid termine la liste par `null` pour absorber la virgule finale.
      this.#tiers = JSON.parse(tiersEl.textContent).filter(Boolean);
    } catch (error) {
      console.error('[yaslie-bundle] données illisibles', error);
      return false;
    }

    return this.#tiers.length > 0;
  }

  /** @returns {HTMLElement[]} tous les blocs « exemplaire » rendus, visibles ou non */
  get #allUnits() {
    return Array.from(this.querySelectorAll('[data-yaslie-unit]'));
  }

  /** @returns {HTMLElement[]} seulement ceux que le palier courant utilise */
  get #activeUnits() {
    return this.#allUnits.filter((unit) => !unit.hidden);
  }

  get #checkedTier() {
    const checked = this.querySelector('[data-yaslie-offer-input]:checked');
    const index = Number(checked?.value);
    return this.#tiers.find((tier) => tier.index === index) ?? this.#tiers[0];
  }

  /** @param {HTMLElement} unit @returns {string[]} */
  #selection(unit) {
    return Array.from(unit.querySelectorAll('[data-yaslie-select]')).map((select) => select.value);
  }

  /** @param {string[]} selection */
  #findVariant(selection) {
    return this.#data.variants.find(
      (variant) =>
        variant.options.length === selection.length &&
        variant.options.every((value, index) => value === selection[index])
    );
  }

  /** @param {HTMLElement} unit */
  #preselect(unit) {
    const firstAvailable = this.#data.variants.find((variant) => variant.available);
    if (!firstAvailable) return;

    unit.querySelectorAll('[data-yaslie-select]').forEach((select, index) => {
      const value = firstAvailable.options[index];
      if (value != null) select.value = value;
    });
  }

  /**
   * Affiche les N premiers exemplaires, renomme leurs légendes et marque
   * ceux qui sont offerts.
   * @param {{units: number, label: string, totalMoney: string}} tier
   */
  #applyTier(tier) {
    if (!tier) return;

    const pack = tier.units > 1;
    this.dataset.mode = pack ? 'pack' : 'single';
    this.dataset.units = String(tier.units);

    const panel = this.querySelector('[data-yaslie-units]');
    if (panel) panel.hidden = !pack;

    // `freeUnits` vient du Liquid : seul un palier « exemplaires offerts »
    // en déclare. Une remise en pourcentage porte sur tout le pack, aucun
    // exemplaire n'y est gratuit.
    const firstFree = tier.units - (tier.freeUnits ?? 0);

    this.#allUnits.forEach((unit, i) => {
      unit.hidden = i >= tier.units;

      const legend = unit.querySelector('[data-yaslie-unit-legend]');
      if (!legend) return;

      legend.textContent = (this.#data.unitTemplate || 'Exemplaire [n]').replace('[n]', String(i + 1));

      if (i >= firstFree && i < tier.units) {
        const free = document.createElement('span');
        free.className = 'yaslie-unit__free';
        free.textContent = this.#data.freeLabel || 'offert';
        legend.append(free);
      }
    });

    if (pack) this.#refreshUnits();
  }

  #handleTierChange = (event) => {
    const index = Number(event.target.value);
    const tier = this.#tiers.find((t) => t.index === index);
    this.#setError(null);
    this.#applyTier(tier);
  };

  #handleSelectChange = () => {
    this.#refreshUnits();
  };

  /**
   * Revalide les exemplaires actifs et met à jour l'état du bouton.
   * Une combinaison inexistante ou épuisée est signalée sur l'exemplaire
   * concerné, pas globalement : la cliente voit lequel corriger.
   */
  #refreshUnits() {
    let blocking = 0;

    for (const unit of this.#activeUnits) {
      const variant = this.#findVariant(this.#selection(unit));
      const error = unit.querySelector('[data-yaslie-unit-error]');

      let message = '';
      if (!variant) message = 'Cette combinaison n’existe pas.';
      else if (!variant.available) message = 'Cette combinaison est momentanément épuisée.';

      if (message) blocking += 1;

      for (const select of unit.querySelectorAll('[data-yaslie-select]')) {
        select.dataset.unavailable = message ? 'true' : 'false';
      }

      if (error) {
        error.textContent = message;
        error.hidden = message === '';
      }
    }

    const submit = this.querySelector('[data-yaslie-bundle-submit]');
    if (submit) submit.setAttribute('aria-disabled', blocking > 0 ? 'true' : 'false');

    return blocking === 0;
  }

  #setError(message) {
    const error = this.querySelector('[data-yaslie-bundle-error]');
    if (!error) return;
    error.textContent = message ?? '';
    error.hidden = !message;
  }

  #handleSubmit = async (event) => {
    event.preventDefault();
    if (this.#submitting) return;

    this.#setError(null);
    if (!this.#refreshUnits()) return;

    const variants = this.#activeUnits.map((unit) => this.#findVariant(this.#selection(unit)));
    if (!variants.length || variants.some((variant) => !variant)) return;

    // Deux exemplaires identiques doivent partir en une seule ligne de
    // quantité 2 : Shopify les fusionnerait de toute façon côté panier, et le
    // retour d'ajout ne correspondrait plus à ce qu'on a envoyé.
    /** @type {Map<number, number>} */
    const merged = new Map();
    for (const variant of variants) {
      merged.set(variant.id, (merged.get(variant.id) ?? 0) + 1);
    }

    const items = Array.from(merged, ([id, quantity]) => ({ id, quantity }));
    const itemCount = variants.length;

    this.#submitting = true;
    const submit = this.querySelector('[data-yaslie-bundle-submit]');
    submit?.setAttribute('aria-disabled', 'true');

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: variants.map((variant) => ({ merchandiseId: String(variant.id), quantity: 1 })),
        promise: deferred.promise,
      })
    );

    try {
      // `fetchConfig` du thème supprime le Content-Type (il sert des FormData) ;
      // `/cart/add.js` en lot exige un corps JSON, on construit donc la requête.
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ items }),
      });
      const payload = await response.json();

      if (payload.status) {
        this.#setError(payload.description || payload.message || 'Ajout au panier impossible.');
        this.dispatchEvent(
          new CartErrorEvent({
            error: payload.message || 'Bundle add to cart failed',
            code: 'INVALID',
            detail: { description: payload.description, errors: payload.errors },
          })
        );
        deferred.resolve({ cart: null, detail: { didError: true, source: 'yaslie-bundle' } });
        return;
      }

      const cart = await this.#refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'yaslie-bundle',
          sourceId: this.id?.toString(),
          itemCount,
          didError: false,
        },
      });
    } catch (error) {
      console.error('[yaslie-bundle]', error);
      this.#setError('Ajout au panier impossible. Réessayez dans un instant.');
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during bundle add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#submitting = false;
      this.#refreshUnits();
    }
  };

  async #refreshCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => response.json());
  }
}

if (!customElements.get('yaslie-bundle')) {
  customElements.define('yaslie-bundle', YaslieBundle);
}
